// =====================================================
// 💬 TECHNICAL CHAT - ASISTENTE TÉCNICO IA CATERPILLAR
// =====================================================
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Send, Loader, Copy, RotateCcw, ThumbsUp, ThumbsDown, Wrench,
  Plus, User, MessageSquare, CheckCircle, XCircle, Bot,
  Sparkles, Clock, BarChart3, ExternalLink, FileText,
  Printer, Download, Search, Trash2, PanelRightClose, PanelRightOpen,Zap,
  Lightbulb, Laptop, X, Printer, Download, ExternalLink, RotateCcw, Loader, AlertCircle, FileText
} from 'lucide-react';

import { Ticket, AlertCircle, X, Printer, Download, ExternalLink, FileText } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore'

import { ticketService, CreateTicketData } from '../services/ticketService';
import { ipService } from '../services/ipService';
import { useTheme } from '../contexts/ThemeContext';
import { askTechnicalQuestion } from '../services/apiService';
import WelcomeMessage from './WelcomeMessage';
import { chatHistoryService, ChatSession } from '../services/chatHistoryService';
import { I18nProvider, useI18n } from '../hooks/useI18n';
import ChatSidebar from './ChatSidebar';
import IAAvatar from '../assets/images/ia.png';
import { useAutoChat } from '../hooks/useAutoChat';
import PartImages from './PartImages';
import { CreateTicketButton, TicketSuggestion, UserTicketStatus } from './TechnicalChatEnhanced';
import iconlight from '../assets/images/icon_light.png';
import iconblack from '../assets/images/icon_black.png';
import { monitoringService } from '../services/monitoringApiService';

// =====================================================
// 🎯 INTERFACES
// =====================================================

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  metadata?: {
    hasStructuredData?: boolean;
    actionButtons?: {
      showCopy: boolean;
      showRegenerate: boolean;
      showLike: boolean;
      showDislike: boolean;
      showDetails: boolean;
    };
    structuredData?: any;
    sources?: any[];
    confidence?: number;
    processingTime?: number;
    userFeedback?: boolean;
    data_source?: string;
    hasButtons?: boolean;
    interactiveButtons?: any[];
    buttonType?: string;
    equipmentCode?: string;
    configuracion?: string;
    sidebarReady?: boolean;
    sidebarData?: any;
     images?: Array<{
      filename: string;
      path: string;
      page?: number;
    }>;
  };
}

interface User {
  id: string;
  name: string;
  email: string;
  token?: string;
}

// 🎨 COMPONENTE TOAST MODERNO
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  isVisible: boolean;
  onClose: () => void;
}

interface TechnicalChatProps {
  currentUser?: { id: string; name?: string; email?: string };
  initialContext?: string;
  language?: string;
  onEngineDetected?: (engine: string) => void;
  onUpgradeRequired?: () => void;
}
// ✅ 1. INTERFAZ DEL COMPONENTE SIDEBAR (agregar después de las otras interfaces)
interface DocumentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  documentData: any;
  isDark: boolean;
  language: string;
}

interface QueryLimitsIndicatorProps {
  limits: any;
  loading: boolean;
  isDark: boolean;
}

const QueryLimitsIndicator: React.FC<QueryLimitsIndicatorProps> = ({ limits, loading, isDark }) => {
  console.log('🎨 [INDICATOR] Renderizando:', { loading, hasLimits: !!limits, limits });
  
  if (loading) {
    console.log('⏳ [INDICATOR] Cargando límites...');
    return null;
  }
  
  if (!limits) {
    console.log('⚠️ [INDICATOR] No hay límites disponibles');
    return null;
  }

  const { usage, subscription } = limits;
  
  if (!usage || !subscription) {
    console.error('❌ [INDICATOR] Estructura de límites inválida:', limits);
    return null;
  }
  
  const isUnlimited = usage.daily_queries_limit === null;
  
  console.log('📊 [INDICATOR] Mostrando indicador:', {
    isUnlimited,
    used: usage.daily_queries_used,
    limit: usage.daily_queries_limit,
    plan: subscription.current_plan
  });
  
  // Calcular porcentaje y color
  let percentage = 0;
  let color = 'bg-green-500';
  
  if (!isUnlimited) {
    percentage = (usage.daily_queries_used / usage.daily_queries_limit) * 100;
    
    if (percentage >= 90) {
      color = 'bg-red-500';
    } else if (percentage >= 70) {
      color = 'bg-yellow-500';
    } else {
      color = 'bg-green-500';
    }
  }

  return (
    <div className={`px-4 py-3 border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-500" />
          <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Consultas hoy:
          </span>
        </div>
        
        {isUnlimited ? (
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-semibold text-green-600 dark:text-green-400">
              ∞ Ilimitado
            </span>
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              ({subscription.current_plan})
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${
                percentage >= 90 ? 'text-red-600 dark:text-red-400' :
                percentage >= 70 ? 'text-yellow-600 dark:text-yellow-400' :
                'text-green-600 dark:text-green-400'
              }`}>
                {usage.daily_queries_used} / {usage.daily_queries_limit}
              </span>
              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                ({usage.daily_queries_remaining} restantes)
              </span>
            </div>
            
            {/* Barra de progreso */}
            <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${color} transition-all duration-300`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Advertencia si está cerca del límite */}
      {!isUnlimited && percentage >= 80 && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <AlertCircle className="w-3 h-3 text-yellow-500" />
          <span className="text-yellow-600 dark:text-yellow-400">
            {percentage >= 90 
              ? '¡Casi sin consultas! Considera actualizar tu plan.' 
              : 'Te estás acercando al límite diario.'}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * 🎨 Formatear mensaje AI: markdown básico → HTML
 */
function formatAIMessage(text: string): string {
  if (!text) return '';
  
  let html = text;
  
  // Si ya contiene tags HTML reales, no reprocesar
  if (/<(strong|em|br|div|h[1-6]|ul|li|p|span|code|table)[\s>/]/i.test(html)) {
    return html;
  }
  
  // **texto** → <strong>
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // *texto* → <em>
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  
  // _texto_ → <em>
  html = html.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');
  
  // `código` → <code>
  html = html.replace(/`([^`]+)`/g, 
    '<code style="background:#1e293b;color:#e2e8f0;padding:1px 5px;border-radius:4px;font-size:0.85em">$1</code>');
  
  // • bullet points
  html = html.replace(/^[•●]\s*(.+)$/gm, 
    '<div style="padding-left:16px;margin:2px 0">• $1</div>');
  
  // \n\n → párrafo
  html = html.replace(/\n\n/g, '</p><p style="margin:8px 0">');
  
  // \n → salto
  html = html.replace(/\n/g, '<br>');
  
  return '<p style="margin:0">' + html + '</p>';
}


const LoadingStates = [
  "Analizando pregunta...",
  "Revisando Base de Conocimientos...",
  "Buscando en Base de Datos...", 
  "Acceso a IA Kikai...",
  "Procesando respuesta..."
];

const ChatHeader = () => (
  <div className="border-b p-4">
    <UserTicketStatus user={currentUser} />
  </div>
);

interface SuggestionCard {
  id: string
  titleKey: string
  descriptionKey: string
  icon: any
  bgColor: string
  iconColor: string
}

// =====================================================
// 📋 SUGGESTION CARDS - ESPECIALIZADO CATERPILLAR
// =====================================================

const SUGGESTION_CARDS: SuggestionCard[] = [
  {
    id: '1',
    titleKey: 'chat.suggestions.expert.title',
    descriptionKey: 'chat.suggestions.expert.description',
    icon: Wrench,
    bgColor: 'bg-gray-50 dark:bg-gray-900/20',
    iconColor: 'text-blue-600 dark:text-blue-400'
  },
  {
    id: '2',
    titleKey: 'chat.suggestions.sales.title',
    descriptionKey: 'chat.suggestions.sales.description',
    icon: BarChart3,
    bgColor: 'bg-gray-50 dark:bg-gray-900/20',
    iconColor: 'text-purple-600 dark:text-purple-400'
  },
  {
    id: '3',
    titleKey: 'chat.suggestions.organized.title',
    descriptionKey: 'chat.suggestions.organized.description',
    icon: Lightbulb,
    bgColor: 'bg-gray-50 dark:bg-gray-900/20',
    iconColor: 'text-orange-600 dark:text-orange-400'
  },
  {
    id: '4',
    titleKey: 'chat.suggestions.code.title',
    descriptionKey: 'chat.suggestions.code.description',
    icon: Laptop,
    bgColor: 'bg-gray-50 dark:bg-gray-900/20',
    iconColor: 'text-green-600 dark:text-green-400'
  }
]

// ✅ COMPONENTE SIDEBAR (autónomo)
const DocumentSidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  documentData: any;
  isLoading?: boolean;
  isDark: boolean;
  language: string;
  onReload?: () => void;
}> = ({
  isOpen,
  onClose,
  documentData,
  isLoading = false,
  isDark,
  language,
  onReload
}) => {
  
  // 🆕 AGREGAR HOOK DE TRADUCCIÓN
  const { t } = useI18n();
  // Cerrar con ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Imprimir
  const handlePrint = useCallback(() => {
    if (!documentData?.content) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const processedContent = documentData.content.replace(
      /<img([^>]*?)src=['"]([^'"]*?)['"]([^>]*?)>/gi,
      (_m, before, src, after) => {
        const absoluteSrc = src.startsWith('http') ? src : `https://sisweb.cat.com${src}`;
        return `<img${before}src="${absoluteSrc}" style="max-width:100%; height:auto;"${after}>`;
      }
    );

    const html = `<!DOCTYPE html>
<html lang="${language}">
<head>
  <meta charset="UTF-8">
  <title>${documentData.title || ''}</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; margin: 20px; color: #333; }
    .header { border-bottom: 2px solid #2563eb; padding-bottom: 12px; margin-bottom: 16px; }
    .title { font-size: 20px; font-weight: 700; color: #1e40af; }
    img { max-width: 100% !important; height: auto !important; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="title">${documentData.title || ''}</div>
    <div>Motor: ${documentData.motor || 'N/A'} | Config: ${documentData.configuracion || 'N/A'}</div>
  </div>
  <div>${processedContent}</div>
</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  }, [documentData, language]);

  // No render si está cerrado
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
     <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />

      {/* Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full w-[800px] max-w-[90vw] transform transition-transform duration-300 z-50 shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${isDark ? 'bg-gray-800 border-l border-gray-700' : 'bg-white border-l border-gray-200'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`flex-shrink-0 p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <div className="flex-1 min-w-0">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {documentData?.title || 'Cargando documento...'}
                </h2>
                {documentData && !isLoading && (
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Motor: {documentData.motor || 'N/A'} • Config: {documentData.configuracion || 'N/A'}
                  </p>
                )}
              </div>

              {/* Cerrar */}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
                className={`p-2 rounded-lg transition-colors hover:scale-105 ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Acciones */}
            {documentData && !isLoading && (
              <div className="flex items-center gap-2 flex-wrap">
                {documentData.content && (
                  <button
                    type="button"
                    onClick={handlePrint}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                      isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-400 text-white'
                    }`}
                  >
                    <Printer className="w-4 h-4" />
                     {t('chat.print')}
                  </button>
                )}

                {documentData.pdfAvailable && documentData.documentId && (
                  <button
                    type="button"
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = `/api/download-pdf/${documentData.documentId}`;
                      a.download = `${documentData.documentId}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                      isDark ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-green-500 hover:bg-green-400 text-white'
                    }`}
                  >
                    <Download className="w-4 h-4" />
                     {t('chat.download')} PDF
                  </button>
                )}
                {onReload && (
                  <button
                    type="button"
                    onClick={onReload}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                      isDark ? 'bg-orange-600 hover:bg-orange-500 text-white' : 'bg-orange-500 hover:bg-orange-400 text-white'
                    }`}
                  >
                    <RotateCcw className="w-4 h-4" />
                    Recargar
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Contenido */}
          
          <div className="flex-1 overflow-auto p-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <Loader className={`w-8 h-8 animate-spin mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Extrayendo contenido del documento...</p>
              </div>
            ) : documentData?.error ? (
              <div className="flex flex-col items-center justify-center h-full">
                <AlertCircle className={`w-12 h-12 mb-4 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                <p className={`mb-4 font-medium ${isDark ? 'text-red-300' : 'text-red-700'}`}>Error al cargar el documento</p>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{documentData.error}</p>
                {onReload && (
                  <button
                    type="button"
                    onClick={onReload}
                    className={`px-4 py-2 rounded-lg ${isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-400 text-white'}`}
                  >
                    Reintentar
                  </button>
                )}
              </div>
            ) : documentData?.content ? (
              <div
                className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
                style={{ color: isDark ? '#e5e7eb' : '#374151', fontSize: '14px', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: documentData.content }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <FileText className={`w-12 h-12 mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Selecciona un documento para ver su contenido</p>
              </div>
            )}
          </div>

          {/* Footer info */}
          {documentData && documentData.pdfAvailable && (
            <div className={`flex-shrink-0 p-3 border-t text-sm ${isDark ? 'border-gray-700 bg-gray-900 text-green-400' : 'border-gray-200 bg-green-50 text-green-700'}`}>
              ✅ PDF generado • {documentData.contentLength} caracteres • {documentData.extractionMethod}
            </div>
          )}
        </div>
      </div>
    </>
  );
};


const Toast: React.FC<ToastProps> = ({ message, type, isVisible, onClose }) => {
  const { isDark } = useTheme();
  // ✅ useEffect siempre ejecutado
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  // ✅ Return condicional DESPUÉS de todos los Hooks
  if (!isVisible) return null;
    
  const getToastStyles = () => {
    const baseStyle = `backdrop-blur-sm border shadow-lg`;
    switch (type) {
      case 'success':
        return `${baseStyle} ${isDark ? 'bg-green-900/90 text-green-100 border-green-700' : 'bg-green-500 text-white border-green-600'}`;
      case 'error':
        return `${baseStyle} ${isDark ? 'bg-red-900/90 text-red-100 border-red-700' : 'bg-red-500 text-white border-red-600'}`;
      default:
        return `${baseStyle} ${isDark ? 'bg-blue-900/90 text-blue-100 border-blue-700' : 'bg-blue-500 text-white border-blue-600'}`;
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      default:
        return <User className="w-5 h-5" />;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className={`${getToastStyles()} px-4 py-3 rounded-xl flex items-center gap-3 min-w-80 max-w-96`}>
        {getIcon()}
        <span className="flex-1 text-sm font-medium">{message}</span>
        <button
          onClick={onClose}
          className="text-current hover:opacity-70 transition-opacity"
        >
          ×
        </button>
      </div>
    </div>
  );
};


// =====================================================
// 📱 COMPONENTE PRINCIPAL
// =====================================================

  // Icono que cambia con el tema usando Tailwind
export function LogoKikai({ className = "w-6 h-6 object-contain" }) {
  return (
    <>
      <img
        src={iconblack}
        alt="Kikai"
        className={`${className} block dark:hidden`}
      />
      {/* El alt vacío evita anuncio duplicado en lectores de pantalla */}
      <img
        src={iconlight}
        alt=""
        aria-hidden="true"
        className={`${className} hidden dark:block`}
      />
    </>
  );
}

// ✅ COMPONENTES DE BOTONES ESTABLES
const SimpleActionButtons = React.memo<{
  message: Message;
  onCopy: () => void;
  onRegenerate: () => void;
  onLike: () => void;
  onDislike: () => void;
}>(({ message, onCopy, onRegenerate, onLike, onDislike }) => {
  const { isDark } = useTheme();
  
  return (
    <div className={`flex items-center justify-between mt-3 pt-3 border-t ${
      isDark ? 'border-gray-600/30' : 'border-gray-200/30'
    }`}>
      <div className="flex items-center gap-1">
        <button
          onClick={onCopy}
          className={`p-2 rounded-lg text-xs transition-colors ${
            isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
          title="Copiar"
        >
          <Copy className="w-3 h-3" />
        </button>
        <button
          onClick={onRegenerate}
          className={`p-2 rounded-lg text-xs transition-colors ${
            isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
          title="Regenerar"
        >
          <RotateCcw className="w-3 h-3" />
        </button>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onLike}
          title="Me gusta"
          className={`p-2 rounded-lg text-xs transition-colors ${
            isDark ? 'text-gray-400 hover:text-green-400' : 'text-gray-500 hover:text-green-600'
          }`}
        >
          <ThumbsUp className="w-3 h-3" />
        </button>
        <button
          onClick={onDislike}
          title="No me gusta"
          className={`p-2 rounded-lg text-xs transition-colors ${
            isDark ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-600'
          }`}
        >
          <ThumbsDown className="w-3 h-3" />
        </button>
             
         {message.metadata?.executionTime && (
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <span>⏱️</span>
          <span>{message.metadata.executionTime}ms</span>
          {message.metadata.sidebarReady && <span>📄 Sidebar</span>}
        </div>
      )}
      </div>
    </div>
  );
});
interface InteractiveButtonsProps {
  message: Message;
  onButtonClick: (query: string, action: string, button?: any) => void;
}

  const simulateProgressiveLoading = async (setStep: (n:number)=>void) => {
  for (let i=0;i<LoadingStates.length;i++){
    setStep(i);
    await new Promise(r=>setTimeout(r, 800 + Math.random()*400));
  }
};

const TechnicalChat: React.FC<TechnicalChatProps> = ({ 
  currentUser: currentUserProp,
  language = 'es',
  onEngineDetected,
  onConfigurationDetected
}) => {
  
  const sidebarRef = useRef<any>(null);
  const { isDark } = useTheme()
  const { t } = useI18n()
  const { user } = useAuthStore()
  const location = useLocation();
  const autoFetchTriggered = useRef(false);
  const { user: storeUser } = useAuthStore();
  const currentUser = currentUserProp ?? storeUser ?? null;
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // Estados del chat
  const [currentChatId, setCurrentChatId] = useState<string>('');
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatSession[]>([])
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [searchHistory, setSearchHistory] = useState('')
  const [historyOpen, setHistoryOpen] = useState(true)
  const [conversationContext, setConversationContext] = useState<any>(null);
  const previousChatIdRef = useRef<string>('');
  const [feedbackSent, setFeedbackSent] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // ✅ ESTADOS BÁSICOS
  const [newMessage, setNewMessage] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
   // Sidebar states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarDocument, setSidebarDocument] = useState<any>(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState<string>('');
  const [preventAutoOpen, setPreventAutoOpen] = useState(false);
  // Estados de límites
  const [userLimits, setUserLimits] = useState<any>(null);
  const [loadingLimits, setLoadingLimits] = useState(false);
  // Toast
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [toastVisible, setToastVisible] = useState(false);
  const maxCharacters = 500;
  const characterCount = newMessage.length;
  const isOverLimit = characterCount > maxCharacters;
  const [closedSidebarMessageId, setClosedSidebarMessageId] = useState<string | null>(null);
  
    // === AUTO-FETCH desde BusquedaNP ===
  useEffect(() => {
    const state = location.state as any;
    if (state?.autoFetchPart && !autoFetchTriggered.current && !isLoading) {
      autoFetchTriggered.current = true;
      const partData = state.autoFetchPart;

      // Limpiar state para evitar re-trigger al navegar
      window.history.replaceState({}, document.title);

      const timer = setTimeout(async () => {
        try {
          setIsLoading(true);
          setLoadingStep(1);

          const token = localStorage.getItem('token') || localStorage.getItem('kikai_token');
          if (!token) throw new Error('Sin token de autenticacion');

          const res = await fetch('/api/parts/fetch-details', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              partNumber: partData.partNumber,
              url: partData.url,
              motorCode: partData.motorCode,
              configCode: partData.configCode,
              language
            })
          });

          if (!res.ok) throw new Error(`Error ${res.status}`);
          const result = await res.json();
          if (!result.success) throw new Error(result.error || 'Error extrayendo detalles');

          // Abrir sidebar con HTML generado por el backend
          const sidebarData = {
            title: `Parte ${partData.partNumber}`,
            motor: partData.motorCode,
            configuracion: partData.configCode,
            content: result.htmlContent || result.answer,
            metadata: {
              queryType: 'part_details',
              partNumber: partData.partNumber,
              hasImage: result.metadata?.hasImage || false,
              imageUrl: result.metadata?.imageUrl || null,
              hasTable: result.metadata?.hasTable || false
            }
          };

          openSidebar(sidebarData);

          // Mensaje breve en chat
          const summaryText = language === 'es'
            ? `Detalles de **${partData.partNumber}**${partData.partName ? ` (${partData.partName})` : ''} disponibles en el panel lateral.`
            : `Details for **${partData.partNumber}**${partData.partName ? ` (${partData.partName})` : ''} in side panel.`;

          const aiMsg: Message = {
            id: (Date.now() + 1).toString(),
            text: summaryText,
            sender: 'ai',
            timestamp: new Date(),
            metadata: {
              hasStructuredData: true,
              sidebarReady: true,
              sidebarData,
              actionButtons: {
                showCopy: true,
                showRegenerate: false,
                showLike: true,
                showDislike: true,
                showDetails: true
              },
              ...result.metadata
            }
          };

          setMessages(prev => [...prev, aiMsg]);

        } catch (err: any) {
          console.error('[AUTO-FETCH] Error:', err);
          const errorMsg: Message = {
            id: Date.now().toString(),
            text: `Error obteniendo detalles de ${partData.partNumber}: ${err.message}`,
            sender: 'ai',
            timestamp: new Date(),
            metadata: {}
          };
          setMessages(prev => [...prev, errorMsg]);
        } finally {
          setIsLoading(false);
          setLoadingStep(0);
        }
      }, 500); // Delay para que el componente se monte completamente

      return () => clearTimeout(timer);
    }
  }, [location.state]);
  // === FIN AUTO-FETCH ===
  
  const closeSidebar = useCallback(() => {
  console.log('📄 [SIDEBAR] Cerrando sidebar...');
  

  // ✅ DETENER AUTO-APERTURA INMEDIATAMENTE
  setPreventAutoOpen(true);
  
  
  // abrir sidebar
const openSidebar = (data: any) => {
  setSidebarDocument(data);
  setSidebarOpen(true);
  setClosedSidebarMessageId(null); // 🆕
};

 
  // ✅ CERRAR SIDEBAR
  setSidebarOpen(false);
  
  // ✅ LIMPIAR DATOS DESPUÉS DE ANIMACIÓN
  setTimeout(() => {
    setSidebarDocument(null);
    console.log('📄 [SIDEBAR] Sidebar cerrado completamente');
  }, 300);
  
}, []);
  
  // Al inicio del componente, agregar verificación de token
  useEffect(() => {
    const token = localStorage.getItem('kikai_token');
    console.log('🔐 Token status:', {
      hasToken: !!token,
      hasCurrentUser: !!currentUser,
      userId: currentUser?.id
    });
    
    if (!token && currentUser) {
      console.warn('⚠️ Usuario logueado pero sin token');
    }
  }, [currentUser]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isLoading, messages])

  useEffect(() => {
    if (user?.id) {
      loadChatHistory()
    }
  }, [user?.id])
   // ✅ EFFECTS
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isLoading]);
  
  useEffect(() => {
  // No procesar si no hay mensajes o está prevenido
  if (messages.length === 0 || preventAutoOpen) return;

  const lastMessage = messages[messages.length - 1];
  
  // Solo abrir si se cumplen TODAS las condiciones
  if (lastMessage.sender === 'ai' && 
      lastMessage.metadata?.sidebarReady && 
      lastMessage.metadata?.sidebarData &&
      !sidebarOpen &&
      closedSidebarMessageId !== lastMessage.id) {

    console.log('🎯 [AUTO-SIDEBAR] Abriendo automáticamente...');
    
    // Abrir con delay pequeño
    setTimeout(() => {
      openSidebar(lastMessage.metadata.sidebarData);
    }, 300);
  }
}, [messages.length, sidebarOpen]); // ✅ QUITAR preventAutoOpen de dependencies
  

// 📊 FUNCIÓN: Cargar límites del usuario
const loadUserLimits = useCallback(async () => {
  if (!currentUser?.id) {
    console.log('⚠️ [LIMITS] No hay usuario actual');
    return;
  }
  
  try {
    setLoadingLimits(true);
    console.log('🔄 [LIMITS] Cargando límites del usuario:', currentUser.id);
    
    const limits = await monitoringService.getUserLimits();
    
    console.log('📊 [LIMITS] Respuesta del servidor:', limits);
    
    // ✅ ACEPTAR RESPUESTA CON O SIN success: true
    if (limits && (limits.success === true || limits.usage)) {
      setUserLimits(limits);
      console.log('✅ [LIMITS] Límites establecidos:', {
        isUnlimited: limits.usage?.daily_queries_limit === null,
        used: limits.usage?.daily_queries_used,
        limit: limits.usage?.daily_queries_limit,
        plan: limits.subscription?.current_plan
      });
    } else {
      console.warn('⚠️ [LIMITS] Respuesta inválida:', limits);
    }
  } catch (error) {
    console.error('❌ [LIMITS] Error cargando límites:', error);
    console.error('Error completo:', error);
  } finally {
    setLoadingLimits(false);
  }
}, [currentUser?.id]);


useEffect(() => {
  if (currentUser?.id) {
    loadUserLimits();
  }
}, [currentUser?.id, loadUserLimits]);


const InteractiveButtons: React.FC<InteractiveButtonsProps> = ({ message, onButtonClick }) => {
  const { isDark } = useTheme();
  
  const interactiveButtons = message.metadata?.interactiveButtons;
  const hasButtons = message.metadata?.hasButtons;
  
  console.log('🎯 [INTERACTIVE-BUTTONS] Renderizando:', {
    hasButtons,
    buttonCount: interactiveButtons?.length
  });
  
  if (!hasButtons || !interactiveButtons || !Array.isArray(interactiveButtons) || interactiveButtons.length === 0) {
    return null;
  }
  
  const getButtonStyle = (style: string) => {
    const baseStyle = "w-full p-2 rounded-xl font-medium text-left transition-all duration-200 hover:scale-85 active:scale-65 shadow-lg flex items-center gap-3";
    
    switch (style) {
      case 'warning':
        return `${baseStyle} bg-gradient-to-r from-yellow-500 to-yellow-500 hover:from-yellow-600 hover:to-yellow-600 text-white`;
      case 'info':
        return `${baseStyle} bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white`;
      case 'success':
        return `${baseStyle} bg-gradient-to-r from-green-500 to-green-500 hover:from-green-600 hover:to-green-600 text-white`;
      default:
        return `${baseStyle} bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white`;
    }
  };
 return (
  <div className="mt-6">
    <div className={`text-sm font-medium mb-4 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
      <span>💡</span>
      <span>Selecciona una opción para continuar:</span>
    </div>
    
    {/* ✅ AGREGAR SCROLL SI HAY +10 BOTONES */}
    <div className={`space-y-3 ${
      interactiveButtons.length > 10 
        ? 'max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-gray-200 dark:scrollbar-track-gray-800' 
        : ''
    }`}>
      {interactiveButtons.map((button: any, index: number) => (
        <button
          key={`btn-${index}-${button.id || button.action}`}
          className={getButtonStyle(button.style)}
          onClick={() => handleButtonClick(button)}
        >
          <div className="flex-grow">
            <div className="font-semibold text-sm">
              {button.text || button.buttonText || button.label}
            </div>
            {button.description && (
              <div className="text-xxs opacity-90 mt-1">
                {button.description}
              </div>
            )}
          </div>
          
          <span className="text-lg opacity-75 flex-shrink-0">→</span>
        </button>
      ))}
    </div>
    
    {/* ✅ MOSTRAR CONTADOR SI HAY MUCHOS BOTONES */}
    {interactiveButtons.length > 10 && (
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
        📋 Mostrando {interactiveButtons.length} opciones (scroll para ver más)
      </div>
    )}
  </div>
);
};
 
const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }, []);
  // =====================================================
  // 🎯 HANDLERS
  // =====================================================
 // ✅ FUNCIÓN saveMessageToDB CORREGIDA
const saveMessageToDB = async (content: string, sender: 'user' | 'ai', chatId: string, metadata?: any) => {
  if (!chatId || !currentUser?.id) {
    console.log('❌ No se puede guardar mensaje - Falta chatId o userId');
    return;
  }

  try {
  
   // ✅ OBTENER IP PÚBLICA
    const publicIp = await ipService.getPublicIP();
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      publicIp: publicIp
    };

    const enrichedMetadata = {
      ...metadata,
      deviceInfo
    };

    await chatHistoryService.saveMessage(chatId, content, sender, enrichedMetadata);
    
    // Refresh sidebar solo si el guardado fue exitoso
    if (sidebarRef.current?.refreshChats) {
      sidebarRef.current.refreshChats();
    }
  } catch (error) {
    console.error('❌ Error guardando mensaje:', error);
    // No mostrar toast de error en cada guardado fallido para no spamear al usuario
  }
};

  // ✅ FUNCIONES DE CHAT
  const createNewChat = useCallback(async () => {
    if (!currentUser?.id) {
      showToast('⚠️ Debes estar logueado', 'info');
      return;
    }

    try {
      const newChatId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log('🆕 [NEW-CHAT] Creando nueva conversación:', newChatId);
      
      setCurrentChatId(newChatId);
      setCurrentSession(null);
      setMessages([]);
      
      try {
        const newSession = await chatHistoryService.createChatSession(
          currentUser.id.toString(), 
          'Nueva conversación'
        );
        setCurrentSession(newSession);
        console.log('✅ [NEW-CHAT] Sesión creada en backend:', newSession.id);
      } catch (backendError) {
        console.warn('⚠️ [NEW-CHAT] Error creando en backend, continuando:', backendError);
      }
      
      if (sidebarRef.current?.refreshChats) {
        sidebarRef.current.refreshChats();
      }
      
      showToast('✨ Nueva conversación iniciada', 'success');
    } catch (error) {
      console.error('❌ [NEW-CHAT] Error:', error);
      showToast('❌ Error creando nueva conversación', 'error');
    }
  }, [currentUser?.id, showToast]);

const loadChatMessages = useCallback(async (chatId: string) => {
  try {
    console.log('📂 [LOAD-CHAT] Cargando mensajes del chat:', chatId);
    
    // ✅ LOG ANTES DE LLAMAR
    console.log('🔍 [LOAD-CHAT] Llamando a getChatSession...');
    
    const session = await chatHistoryService.getChatSession(chatId);
    
    // ✅ LOG DESPUÉS DE LLAMAR
    console.log('🔍 [LOAD-CHAT] getChatSession respondió:', session);
    console.log('🔍 [LOAD-CHAT] session.messages:', session?.messages);
    console.log('🔍 [LOAD-CHAT] messages.length:', session?.messages?.length);
    
    if (session && session.messages) {
      // ✅ LOG DE MENSAJES ANTES DE MAPEAR
      console.log('🔍 [LOAD-CHAT] Primer mensaje crudo:', session.messages[0]);
      
      const formattedMessages: Message[] = session.messages.map((msg, index) => {
        const formatted = {
          id: msg.id || Date.now().toString(),
          text: msg.text || msg.content || '[sin texto]',  // ← AGREGAR FALLBACK
          sender: msg.isUser ? 'user' : 'ai',
          timestamp: new Date(msg.timestamp || msg.created_at || Date.now()),
          metadata: msg.metadata || {}
        };
        
        // ✅ LOG PRIMER MENSAJE FORMATEADO
        if (index === 0) {
          console.log('🔍 [LOAD-CHAT] Primer mensaje formateado:', formatted);
        }
        
        return formatted;
      });
      
      console.log('🔍 [LOAD-CHAT] Total mensajes formateados:', formattedMessages.length);
      console.log('🔍 [LOAD-CHAT] Textos:', formattedMessages.map(m => m.text?.substring(0, 20)));
      
      setMessages(formattedMessages);
      setCurrentSession(session);
      setCurrentChatId(chatId);
      
      console.log(`✅ [LOAD-CHAT] ${formattedMessages.length} mensajes cargados`);
    } else {
      console.error('❌ [LOAD-CHAT] session o session.messages es null/undefined');
      console.log('🐛 [DEBUG] session:', session);
    }
  } catch (error) {
    console.error('❌ [LOAD-CHAT] Error cargando mensajes:', error);
    console.error('❌ [LOAD-CHAT] Error stack:', error.stack);
    showToast('❌ Error cargando historial del chat', 'error');
  }
}, [showToast]);


  const handleChatSelect = useCallback((chatId: string) => {
    console.log('🎯 [CHAT-SELECT] Chat seleccionado:', chatId);
    if (chatId !== currentChatId) {
      loadChatMessages(chatId);
    }
  }, [currentChatId, loadChatMessages]);

type ChatButton = {
  id?: string;
  action?: string;
  query?: string;
  label?: string;
  text?: string;
  payload?: any;
  data?: any;
  [k: string]: any;
};

// TechnicalChat.tsx - handleButtonClick (RESTAURAR ORIGINAL)

const handleButtonClick = useCallback(async (button: ChatButton) => {
  if (isLoading) return;

  const action = button.action ?? 'select';
  const text =
    (typeof button.query === 'string' && button.query) ||
    (typeof button.text === 'string' && button.text) ||
    (typeof button.label === 'string' && button.label) ||
    (typeof button.payload === 'string' && button.payload) || '';

  const buttonData = button.data ?? button.payload ?? button;

  // Asegurar chat activo
  let activeChatId = currentChatId;
  if (!activeChatId) {
    const baseTitle = text || 'Nueva conversación';
    const chatTitle = baseTitle.length > 50 ? baseTitle.slice(0, 47) + '…' : baseTitle;
    const newSession = await chatHistoryService.createChatSession(currentUser!.id.toString(), chatTitle);
    activeChatId = newSession.id;
    setCurrentChatId(activeChatId);
    setCurrentSession(newSession);
    sidebarRef.current?.refreshChats?.();
    showToast('✨ Nueva conversación iniciada', 'success');
  }

  // Cerrar sidebar si la acción lo requiere
  if (action === 'navigate_to_configuration_and_find_part') {
    setSidebarOpen(false);
    setSidebarDocument(null);
  }

  try {
    setIsLoading(true);
    setLoadingStep(1);

    const needsButtonData = [
      'select_configuration','show_parts','show_services','show_repair','show_3d','show_dashboard',
      'show_information','open_dashboard_document','open_parts_section','explore_parts_component',
      'show_part_details','navigate_to_configuration','explore_repair_system','explore_repair_section',
      'view_repair_procedure','explore_service_system','expand_service_component','view_service_item',
      'show_service_details','show_troubleshooting','explore_troubleshooting_system',
      'view_troubleshooting_code','navigate_troubleshooting_link',
      'navigate_to_configuration_and_find_part',
      'show_family',          // ✅ AGREGAR
      'back_to_motor',        // ✅ AGREGAR
      'show_general_info',    // ✅ AGREGAR
      'show_menu_data', 'fetch_part_details',       // ✅ AGREGAR
      'create_ticket' 
    ].includes(action);

      if (action === 'create_ticket' || button.id === 'create-ticket' || 
         (text && text.includes('ticket'))) {
      // Construir URL con parámetros
      const params = new URLSearchParams();
      const data = button.data || buttonData || {};
      if (data.motor) params.append('motor', data.motor);
      if (data.configuracion) params.append('configuration', data.configuracion);
      if (data.query) params.append('description', data.query);
      
      const url = params.toString() 
        ? `/dashboard/tickets/create?${params.toString()}`
        : '/dashboard/tickets/create';
      
      window.location.href = url;
      return;
    }

    // 🔗 OPEN_URL: Abrir enlace externo (NO enviar al backend)
     if (action === 'fetch_part_details') {
      try {
        setLoadingStep(1);
        showToast('✅ Extrayendo detalles de la parte...', 'info');

        // ✅ OBTENER TOKEN DE AUTENTICACIÃ"N
        const token = localStorage.getItem('kikai_token');
        if (!token) {
          throw new Error('No se encontró token de autenticación');
        }

        const partDetailsRes = await fetch('/api/parts/fetch-details', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`  // ✅ AGREGAR TOKEN
          },
          body: JSON.stringify({
            partNumber: buttonData?.partNumber || buttonData?.data?.partNumber,
            url: buttonData?.url || buttonData?.data?.url,
            motorCode: buttonData?.motorCode || buttonData?.data?.motorCode,
            configCode: buttonData?.configCode || buttonData?.data?.configCode,
            language
          })
        });

        if (!partDetailsRes.ok) {
          throw new Error(`Error ${partDetailsRes.status}: ${partDetailsRes.statusText}`);
        }

        const result = await partDetailsRes.json();
        
        if (!result.success) {
          throw new Error(result.error || 'No se pudo extraer información');
        }

  // Abrir sidebar con HTML del backend
        const partNum = buttonData?.partNumber || buttonData?.data?.partNumber || 'N/A';
        const mCode = buttonData?.motorCode || buttonData?.data?.motorCode || '';
        const cCode = buttonData?.configCode || buttonData?.data?.configCode || '';

        const sidebarData = {
          title: `Parte ${partNum}`,
          motor: mCode,
          configuracion: cCode,
          content: result.htmlContent || result.answer || result.respuesta,
          metadata: {
            queryType: 'part_details',
            partNumber: partNum,
            hasImage: result.metadata?.hasImage || false,
            imageUrl: result.metadata?.imageUrl || null,
            hasTable: result.metadata?.hasTable || false,
            extractedAt: new Date().toISOString()
          }
        };

        openSidebar(sidebarData);

        // Mensaje breve en chat
        const summaryText = language === 'es'
          ? `Detalles de la parte **${partNum}**${result.data?.partTitle ? ` (${result.data.partTitle})` : ''} disponibles en el panel lateral.${result.data?.partTable?.length ? ` ${result.data.partTable.length} piezas individuales encontradas.` : ''}${result.data?.partImage ? ' Imagen disponible.' : ''}`
          : `Part **${partNum}** details${result.data?.partTitle ? ` (${result.data.partTitle})` : ''} available in side panel.${result.data?.partTable?.length ? ` ${result.data.partTable.length} individual parts found.` : ''}${result.data?.partImage ? ' Image available.' : ''}`;

        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: summaryText,
          sender: 'ai',
          timestamp: new Date(),
          metadata: {
            hasStructuredData: true,
            sidebarReady: true,
            sidebarData: sidebarData,
            actionButtons: {
              showCopy: true,
              showRegenerate: false,
              showLike: true,
              showDislike: true,
              showDetails: true
            },
            ...result.metadata
          }
        };

        setMessages(prev => [...prev, aiMessage]);
        await chatHistoryService.saveMessage(activeChatId, summaryText, 'ai', aiMessage.metadata);

        showToast('Detalles extraidos correctamente', 'success');
        setIsLoading(false);
        setLoadingStep(0);
        return;



      } catch (err: any) {
        console.error('❌ [FETCH-PART-DETAILS] Error:', err);
        showToast(`❌ Error: ${err.message}`, 'error');
        setIsLoading(false);
        setLoadingStep(0);
        return;
      }
    }

    // ⚙️ SELECT_MOTOR: Enviar motor como consulta (dispara extractMotorCodeSafe)
    if (action === 'select_motor') {
      const motorCode = buttonData?.motorCode || buttonData?.data?.motorCode || buttonData?.motor;
      if (motorCode) {
        setIsLoading(false);
        setLoadingStep(0);
        const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
        await handleSendMessage(fakeEvent, motorCode);
        return;
      }
    }

    if (needsButtonData && buttonData) {
      const res = await fetch('/api/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          buttonData: { action, data: buttonData },
          language,
          sessionId: activeChatId
        }),
      });
      if (!res.ok) throw new Error(`Backend error: ${res.status}`);
      const result = await res.json();

      const aiMessage: Message = {
        id: (Date.now()+1).toString(),
        text: result.answer || result.respuesta || 'Acción procesada',
        sender: 'ai',
        timestamp: new Date(),
        metadata: {
          hasStructuredData: false,
          actionButtons: { showCopy:true, showRegenerate:true, showLike:true, showDislike:true, showDetails:false },
          ...result.metadata
        }
      };

      // Mensaje del usuario (para persistencia)
      const userMessage: Message = {
        id: Date.now().toString(),
        text,
        sender: 'user',
        timestamp: new Date(),
        metadata: {}
      };

      setMessages(prev => [...prev, aiMessage]);
      await chatHistoryService.saveMessage(activeChatId, userMessage.text, 'user', userMessage.metadata);
      await chatHistoryService.saveMessage(activeChatId, aiMessage.text, 'ai', aiMessage.metadata);
      showToast('✅ Acción procesada', 'success');
   } else {
        // Si no requiere buttonData, reusa flujo normal
        const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
        await handleSendMessage(fakeEvent, text);
      }
  } catch (err:any) {
    console.error('❌ [BUTTON-HANDLER] Error:', err);
    showToast(`❌ Error: ${err.message ?? err}`, 'error');
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text: `❌ Error procesando ${action}: ${err.message ?? err}`,
      sender: 'ai',
      timestamp: new Date(),
      metadata: {}
    }]);
  } finally {
    setIsLoading(false);
    setLoadingStep(0);
  }
}, [isLoading, currentChatId, language, currentUser?.id, showToast]);
   

const loadChatHistory = async () => {
    try {
      const groups = await chatHistoryService.getUserChatsGrouped(user!.id.toString())
      const allChats = groups.flatMap(g => g.chats)
      setChatHistory(allChats)
    } catch (error) {
      console.error('Error loading history:', error)
    }
  }
  // ✅ FUNCIONES DE SIDEBAR
const openSidebar = useCallback((documentData: any) => {
  console.log('📄 [SIDEBAR-OPEN] Abriendo sidebar con documento:', {
    title: documentData?.title || 'Sin título',
    motor: documentData?.motor || 'N/A',
    hasContent: !!(documentData?.content)
  });
  // ✅ RESETEAR PREVENCIÓN AL ABRIR MANUALMENTE
  setPreventAutoOpen(false);
  
  setSidebarDocument(documentData);
  setSidebarOpen(true);
}, []);
// ✅ 3. FUNCIÓN closeSidebar CORREGIDA (SIN BUCLE)
useEffect(() => {
  if (preventAutoOpen && !sidebarOpen) {
    const timer = setTimeout(() => {
      console.log('🔄 [RESET] Habilitando auto-apertura');
      setPreventAutoOpen(false);
    }, 8000);
    return () => clearTimeout(timer);
  }
}, [preventAutoOpen, sidebarOpen]);

const handleDownload = useCallback(() => {
  if (!sidebarDocument) {
    console.warn('⚠️ [DOWNLOAD] No hay documento disponible');
    return;
  }

  console.log('📄 [DOWNLOAD] Iniciando descarga:', {
    documentId: sidebarDocument.documentId,
    hasPDF: !!sidebarDocument.pdfPath,
    pdfGenerated: sidebarDocument.metadata?.pdfGenerated
  });

  // ✅ PRIORIDAD 1: PDF EXISTENTE
  if (sidebarDocument.documentId && sidebarDocument.metadata?.pdfGenerated) {
    console.log('📄 [DOWNLOAD] Descargando PDF existente...');
    
    const link = document.createElement('a');
    link.href = `/api/download-pdf/${sidebarDocument.documentId}`;
    link.download = `${sidebarDocument.motor}-${sidebarDocument.configuracion}.pdf`;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('📄 Descargando PDF...', 'info');
    return;
  }

  // ✅ PRIORIDAD 2: GENERAR PDF DINÁMICAMENTE
  if (sidebarDocument.content) {
    console.log('📄 [DOWNLOAD] Generando PDF dinámicamente...');
    showToast('⏳ Generando PDF...', 'info');
    
    fetch('/api/generate-pdf-download', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: sidebarDocument.documentId,
        htmlContent: sidebarDocument.content,
        documentInfo: {
          title: sidebarDocument.title,
          motor: sidebarDocument.motor,
          configuracion: sidebarDocument.configuracion
        }
      })
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.blob();
    })
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sidebarDocument.motor}-${sidebarDocument.configuracion}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      showToast('✅ PDF descargado', 'success');
    })
    .catch(error => {
      console.error('❌ [DOWNLOAD] Error:', error);
      showToast('❌ Error generando PDF', 'error');
    });
  }
}, [sidebarDocument, showToast]);  
  // 🔧 Utility: Obtener emoji según tipo de archivo
const getFileEmoji = useCallback((filePath: string): string => {
  if (!filePath) return '📄';
  const extension = filePath.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf': return '📄';
    case 'doc':
    case 'docx': return '📝';
    case 'xls':
    case 'xlsx': return '📊';
    case 'txt': return '📃';
    case 'jpg':
    case 'jpeg':
    case 'png': return '🖼️';
    default: return '📁';
  }
}, []);
// 🔧 Utility: Formatear confianza
const formatConfidence = useCallback((confidence: number): string => {
  return `${(confidence * 100).toFixed(0)}%`;
}, []);
// 🔧 Utility: Color según confianza
const getConfidenceColor = useCallback((confidence: number): string => {
  if (confidence >= 0.8) return 'text-green-600';
  if (confidence >= 0.6) return 'text-yellow-600';
  return 'text-red-600';
}, []);
// 🔧 Utility: Manejar Enter key
const handleKeyPress = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    handleSendMessage(e);
  }
};

// ====================================================================
// PARTE 4: debugMetadataFlow (Después de utilities, antes de handleSendMessage)
// ====================================================================

const debugMetadataFlow = (response: any, aiMessage: Message) => {
  console.group('🛠 [METADATA-FLOW-DEBUG]');
  
  console.log('📥 Backend Response:', {
    hasMetadata: !!response.metadata,
    metadataKeys: response.metadata ? Object.keys(response.metadata) : [],
    hasButtons: response.metadata?.hasButtons,
    interactiveButtons: response.metadata?.interactiveButtons,
    sampleButton: response.metadata?.interactiveButtons?.[0]
  });
  
  console.log('📤 Frontend Message:', {
    hasMetadata: !!aiMessage.metadata,
    metadataKeys: aiMessage.metadata ? Object.keys(aiMessage.metadata) : [],
    hasButtons: aiMessage.metadata?.hasButtons,
    interactiveButtons: aiMessage.metadata?.interactiveButtons,
    sampleButton: aiMessage.metadata?.interactiveButtons?.[0]
  });
  
  console.log('🔍 Comparison:', {
    backendHasButtons: response.metadata?.hasButtons,
    frontendHasButtons: aiMessage.metadata?.hasButtons,
    backendButtonCount: response.metadata?.interactiveButtons?.length || 0,
    frontendButtonCount: aiMessage.metadata?.interactiveButtons?.length || 0,
    dataFlowOk: response.metadata?.hasButtons === aiMessage.metadata?.hasButtons
  });
  
  console.groupEnd();
};
  
const handleSendMessage = useCallback(async (e: React.FormEvent, customMessage?: string) => {
  e.preventDefault();
 
 setIsLoading(true);
  setLoadingStep(0);

 await simulateProgressiveLoading(setLoadingStep); 
 console.log('current:'+currentUser);
 console.log('newmessage:'+newMessage); 
 
  const messageToSend = customMessage || newMessage;
  
  if (!messageToSend.trim() || !currentUser || isLoading) {
    return;
  }
  
  const messageText = messageToSend.trim();
 const userMessage: Message = {
                id: Date.now().toString(),
                text: messageText,
                sender: 'user',
                timestamp: new Date(),
              };
              
let activeChatId = currentChatId;
    console.log('🆕 [handleSendMessage] chat ID inicial!:', activeChatId);
    if (!activeChatId) {
      try {
        console.log('🆕 Creando nueva sesión...');
        
        const chatTitle = messageText.length > 50 
          ? messageText.substring(0, 47) + '...'
          : messageText;
        
        const newSession = await chatHistoryService.createChatSession(
          currentUser.id.toString(), 
          chatTitle
        );
        
        
        activeChatId = newSession.id;
        setCurrentChatId(activeChatId);
        setCurrentSession(newSession);
        
        if (!activeChatId) {
              try {
                console.log('🆕 Creando nueva sesión...');
                
                const chatTitle = messageToSend.length > 50 
                  ? messageToSend.substring(0, 47) + '...'
                  : messageToSend;
                
                const newSession = await chatHistoryService.createChatSession(
                  currentUser.id.toString(), 
                  chatTitle
                );
                
                activeChatId = newSession.id;
                setCurrentChatId(activeChatId);
                setCurrentSession(newSession);
                
                console.log('✅ Nueva sesión creada:', activeChatId);
                
                if (sidebarRef.current?.refreshChats) {
                  sidebarRef.current.refreshChats();
                }
                
                showToast?.('✨ Nueva conversación iniciada', 'success');
              } catch (error) {
                console.error('❌ Error creando nueva sesión:', error);
                showToast?.('❌ Error creando nueva conversación', 'error');
                return;
              }
            }
            
            if (!activeChatId) {
              console.error('❌ No hay chat activo para guardar mensajes');
              showToast?.('❌ Error: No hay conversación activa', 'error');
              return;
            }
        
        console.log('✅ Nueva sesión creada:', activeChatId);
        
        if (sidebarRef.current?.refreshChats) {
          sidebarRef.current.refreshChats();
        }
        
        showToast('✨ Nueva conversación iniciada', 'success');
      } catch (error) {
        console.error('❌ Error creando nueva sesión:', error);
        showToast('❌ Error creando nueva conversación', 'error');
        return;
      }
    }

  
   
  setMessages(prev => [...prev, userMessage]);
   setNewMessage('');
   setIsLoading(true);
   console.log('🚀 [handleSendMessage] Enviando consulta:', messageText);
     // ✅ GUARDAR MENSAJE DEL USUARIO EN BD
   await saveMessageToDB(userMessage.text, 'user', activeChatId, userMessage.metadata);

  try {
      console.log('🚀 [SEND] Enviando consulta:', messageText);
      const startTime = performance.now();
      const response = await askTechnicalQuestion(messageText, {
        context: null,
        language: language,
        userId: user.id,
        chatHistory: messages.slice(-6),
        sessionId: activeChatId
      });
      
    console.log('📨 [SEND] Respuesta recibida:', response);

    // 🎯 EXTRAER TEXTO DE RESPUESTA
     let responseText = '';
     let metadata = {};
     
     if (response.result) {
        responseText = response.result.answer || response.result.respuesta || 'Respuesta procesada';
        metadata = response.result.metadata || {};
      } else if (response.answer || response.respuesta) {
        responseText = response.answer || response.respuesta;
        metadata = response.metadata || {};
      } else {
        responseText = 'Respuesta recibida correctamente';
        metadata = response;
      }

    // ✅✅✅ METADATA MAPPING EXPLÍCITO Y CORRECTO ✅✅✅
     const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        sender: 'ai',
        timestamp: new Date(),
        metadata: {
          hasStructuredData: false,
          actionButtons: {
            showCopy: true,
            showRegenerate: true,
            showLike: true,
            showDislike: true,
            showDetails: false
          },
          ...metadata
        }
      };

    const executionTime = performance.now() - startTime;

      const messageWithTiming = {
        ...aiMessage, // mensaje existente
        metadata: {
          ...aiMessage.metadata,
          executionTime: Math.round(executionTime),
          timestamp: new Date().toISOString()
        }
      };
      
      setMessages(prev => [...prev, messageWithTiming]); 
    
    // ✅ Guardar en BD
    try {
        await saveMessageToDB(messageText, 'user', activeChatId);
        await saveMessageToDB(responseText, 'ai', activeChatId, aiMessage.metadata);
      } catch (saveError) {
        console.warn('⚠️ [SEND] Error guardando mensajes:', saveError);
      }
     loadUserLimits();
  } catch (error) {
    console.error('❌ Error enviando mensaje:', error);
    
    const errorMessage: Message = {
      id: (Date.now() + 1).toString(),
      text: 'Lo siento, ha ocurrido un error al procesar tu consulta. Por favor, intenta nuevamente.',
      sender: 'ai',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, errorMessage]);
    
  } finally {
    setIsLoading(false);
  }
 }, [newMessage, isLoading, currentChatId, language, currentUser?.id, saveMessageToDB, showToast]);

const handleSuggestionClick = (suggestion: SuggestionCard) => {
    const translatedText = t(suggestion.titleKey)
    const fakeEvent = { preventDefault: () => {} } as React.FormEvent
    handleSendMessage(fakeEvent, translatedText)
  }

   // ✅ FUNCIONES DE ACTION BUTTONS
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('📋 Respuesta copiada al portapapeles', 'success');
    } catch (error) {
      console.error('Error copiando:', error);
      showToast('❌ Error al copiar texto', 'error');
    }
  };
  
  const useFocusInput = () => {
  const inputRef = useRef(null);

  const focusInput = useCallback(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, []);

  return { inputRef, focusInput };
};

  const handleRegenerate = async (originalQuestion: string) => {
    if (!currentUser) return;
    
    setIsLoading(true);
    setLoadingStep(0);
    
    try {
      simulateProgressiveLoading();
      
      const response = await askTechnicalQuestion(originalQuestion + ' (regenerar respuesta)', {
        context: initialContext,
        language: language,
        userId: currentUser.id,
        chatHistory: messages.slice(-6),
        sessionId: currentSessionId
      });
      
      const regeneratedMessage: Message = {
          id: Date.now().toString(),
          text: response.response || response.answer || 'Error regenerando respuesta', // ← CORREGIDO
          sender: 'ai',
          timestamp: new Date(),
          metadata: response.chatMetadata || response.metadata
        };
      setMessages(prev => [...prev, regeneratedMessage]);
    } catch (error) {
      console.error('Error regenerando:', error);
      showToast('❌ Error regenerando respuesta', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFeedback = async (messageId: string, isPositive: boolean) => {
    if (feedbackSent.has(messageId)) return;
    
    setFeedbackSent(prev => new Set(prev).add(messageId));
    
    try {
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, metadata: { ...msg.metadata, userFeedback: isPositive } }
          : msg
      ));

      if (isPositive) {
        showToast('✅ Gracias por tu feedback positivo', 'success');
      } else {
        showToast('📝 Gracias por tu feedback, nos ayuda a mejorar', 'info');
      }

    } catch (error) {
      console.error('Error enviando feedback:', error);
      showToast('❌ Error al enviar feedback', 'error');
    }
  };
  
  const handleDetails = (message: Message) => {
    if (message.metadata?.structuredData) {
      console.log('Datos estructurados:', message.metadata.structuredData);
      showToast('📋 Ver detalles en consola del navegador (F12)', 'info');
    }
  };
  // ✅ EFFECTS
useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isLoading]);

useEffect(() => {
  // No procesar si no hay mensajes o está prevenido
  if (messages.length === 0 || preventAutoOpen) return;

  const lastMessage = messages[messages.length - 1];
  
  // Solo abrir si se cumplen TODAS las condiciones
 if (lastMessage.sender === 'ai' && 
      lastMessage.metadata?.sidebarReady && 
      lastMessage.metadata?.sidebarData &&
      !sidebarOpen &&
      closedSidebarMessageId !== lastMessage.id) {

    console.log('🎯 [AUTO-SIDEBAR] Abriendo automáticamente...');
    
    // Abrir con delay pequeño
    setTimeout(() => {
      openSidebar(lastMessage.metadata.sidebarData);
    }, 300);
  }
}, [messages.length, sidebarOpen]); // ✅ QUITAR preventAutoOpen de dependencies

useEffect(() => {
  const handleConfigButtonClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const button = target.closest('.config-nav-btn') as HTMLElement;
    
    if (button && sidebarOpen) {
      console.log('🚜 [CONFIG-BTN] Click detectado en botón de configuración');
      
      // Prevenir propagación para evitar cerrar sidebar
      event.preventDefault();
      event.stopPropagation();
      
      const motor = button.getAttribute('data-motor');
      const config = button.getAttribute('data-config');
      const partNumber = button.getAttribute('data-part');
      const fullText = button.getAttribute('data-text');
      
      if (motor && config && partNumber) {
        console.log('🚜 [CONFIG-BTN] Datos extraídos:', { motor, config, partNumber });
        
        // Cambiar visual del botón inmediatamente
        const originalContent = button.innerHTML;
        button.style.background = 'linear-gradient(135deg, #f59e0b, #d97706)';
        button.innerHTML = '<div style="text-align: center; padding: 10px;"><span style="font-size: 18px;">⏳</span> <span style="margin-left: 8px;">Navegando...</span></div>';
        button.disabled = true;
        
        // Ejecutar búsqueda interna después de un breve delay
        setTimeout(() => {
          handleButtonClick(
            'navigate_to_configuration_and_find_part',
            fullText || `Navegar a ${motor}-${config} (${partNumber})`,
            {
              action: 'navigate_to_configuration_and_find_part',
              motor,
              configuracion: config,
              originalPartNumber: partNumber,
              fullText,
              level: 'full_configuration_navigation',
              isInternal: true // <— marcamos que es acción interna
            }
          ).catch(error => {
            console.error('❌ [CONFIG-BTN] Error en navegación:', error);
            // Restaurar botón en caso de error
            button.style.background = 'linear-gradient(135deg, #dc2626, #b91c1c)';
            button.innerHTML = '<div style="text-align: center; padding: 10px;">❌ Error - Reintenta</div>';
            button.disabled = false;
          });
        }, 100);
      }
    }
  };
  
  // Agregar listener específico para clics en el sidebar
  if (sidebarOpen) {
    document.addEventListener('click', handleConfigButtonClick, true); // useCapture = true
  }
  
  return () => {
    document.removeEventListener('click', handleConfigButtonClick, true);
  };
}, [handleButtonClick, sidebarOpen]);

  const handleNewChat = () => {
    setMessages([])
    setCurrentChatId(null)
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

const handleLoadChat = async (chatId: string) => {
  try {
    console.log('🎯 [HANDLE-LOAD] Cargando chat:', chatId);
    setCurrentChatId(chatId)
    
    // ✅ USAR getChatSession en lugar de getChatMessages
    const session = await chatHistoryService.getChatSession(chatId)
    
    console.log('🎯 [HANDLE-LOAD] Session obtenida:', session?.messages?.length, 'mensajes');
    
    if (session && session.messages) {
      // ✅ Los mensajes ya vienen mapeados de getChatSession
      const formattedMessages: Message[] = session.messages.map(msg => ({
        id: msg.id || Date.now().toString(),
        text: msg.text || '',  // ✅ Ya viene como 'text' de getChatSession
        sender: msg.isUser ? 'user' : 'ai',
        timestamp: new Date(msg.timestamp),
        metadata: msg.metadata || {}
      }))
      
      console.log('🎯 [HANDLE-LOAD] Primer mensaje:', formattedMessages[0]?.text?.substring(0, 30));
      
      setMessages(formattedMessages)
      console.log('✅ [HANDLE-LOAD] Mensajes cargados');
    }
  } catch (error) {
    console.error('❌ [HANDLE-LOAD] Error loading chat:', error)
  }
}

  const handleDeleteChat = async (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await chatHistoryService.deleteChatSession(chatId)
      setChatHistory(prev => prev.filter(chat => chat.id !== chatId))
      if (currentChatId === chatId) {
        handleNewChat()
      }
    } catch (error) {
      console.error('Error deleting chat:', error)
    }
  }

  const filteredHistory = chatHistory.filter(chat =>
    chat.title.toLowerCase().includes(searchHistory.toLowerCase())
  )
  
  const styles = `
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  .animate-fadeOut {
    animation: fadeOut 0.3s ease-out forwards;
  }
`;

const formatMessageText = (text: string) => {
  if (!text) return '';
  
  // Convertir **texto** a <strong>texto</strong>
  return text.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold text-blue-600 dark:text-blue-400">$1</strong>');
};


  // =====================================================
  // 🎨 RENDER
  // =====================================================

  return (
    <div className="h-[calc(100vh-8.2rem)] flex gap-6">
      
      {/* ============================================= */}
      {/* 💬 CARD DEL CHAT */}
      {/* ============================================= */}
      <div className={`${historyOpen ? 'flex-[7]' : 'flex-1'} flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden transition-all duration-300`}>
        
        {/* ✅ Header con título especializado */}
        <div className="h-16 px-6 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <LogoKikai className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('chat.title')}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('chat.subtitle')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleNewChat}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" title="{t('chat.history.newChat')}" />
             
            </button>
              <button
              onClick={() => setHistoryOpen(!historyOpen)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title={historyOpen ? t('chat.hideHistory') : t('chat.showHistory')}
            >
              {historyOpen ? (
                <PanelRightClose className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <PanelRightOpen className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
        </div>
         {/* ✅ INDICADOR DE LÍMITES */}
            <QueryLimitsIndicator 
              limits={userLimits} 
              loading={loadingLimits}
              isDark={isDark}
            />
        {/* Área de mensajes */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* ✅ Welcome Message - Tamaños reducidos */}
         {messages.length === 0 && !isLoading && !newMessage.trim() && (
            <div className="max-w-4xl mx-auto animate-fadeOut">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4">
                 <LogoKikai />
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  <span className="text-blue-600 dark:text-blue-400">
                    {t('chat.welcome.hello', { name: user?.name || 'Usuario' })}
                  </span>{' '}
                  <span className="text-xl">👋</span>
                </h2>
                <p className="text-base text-gray-600 dark:text-gray-400">
                  {t('chat.welcome.subtitle')}
                </p>
              </div>

              {/* ✅ Suggestion Cards - Reducidos */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {SUGGESTION_CARDS.map(card => {
                  const IconComponent = card.icon
                  return (
                    <button
                      key={card.id}
                      onClick={() => handleSuggestionClick(card)}
                      className={`p-5 rounded-xl border border-gray-200 dark:border-gray-700 ${card.bgColor} text-left hover:shadow-md transition-all duration-200 group`}
                    >
                    <div className={`p-2 rounded-lg ${card.iconColor}`}>
                          <IconComponent className="w-5 h-5" />
                     </div>
                      <div className="flex items-start gap-1">
                        <div className="flex-1 text-center">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {t(card.titleKey)}
                          </h3>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {t(card.descriptionKey)}
                          </p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Mensajes */}
          {messages.map((message, i) => {
           const key =
            message.id ??
            message.uuid ??
            message._id ??
            (message.timestamp ? `${+new Date(message.timestamp)}-${i}` : `m-${i}`);
          return (
            <div
            key={key}
              className={`flex gap-3 mb-4 ${
                message.sender === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message.sender === 'ai' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                 <img 
                     src={IAAvatar} 
                     alt="KIKAI Técnico Caterpillar" 
                     className="w-6 h-6 object-contain"
                   />
                </div>
              )}
              
              <div className={`flex-1 max-w-6xl rounded-2xl p-4 ${
                message.sender === 'user'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
              }`}>
              <div className="ai-message-content">
              {/* Texto del mensaje */}
                    <div className="prose prose-sm max-w-none">
                      {(() => {
                        const text = message.text || message.content || '';
                    
                        // AI: siempre parsear markdown/HTML
                        if (message.sender === 'ai') {
                          return (
                            <div
                              className="ai-message-content"
                              dangerouslySetInnerHTML={{ __html: formatAIMessage(text) }}
                            />
                          );
                        }
                    
                        // User: texto plano
                        return text
                          .split('\n')
                          .filter((line: string) => line.trim() !== '')
                          .map((line: string, i: number) => (
                            <p key={i} className="mb-2 last:mb-0">
                              {line}
                            </p>
                          ));
                      })()}
                    </div>
                
                {/* Imágenes de partes (si existen) */}
                {message.sender === 'ai' && message.metadata?.images && (
                  <PartImages 
                    images={message.metadata.images} 
                    isDark={isDark} 
                  />
                )}
              {/* Botones interactivos */}
             {message.sender === 'ai'
                     && message.metadata?.hasButtons
                     && Array.isArray(message.metadata?.interactiveButtons)
                     && message.metadata.interactiveButtons.length > 0 && (
                       <>
                         <InteractiveButtons
                           message={message}
                          onButtonClick={(button) => {
                             handleButtonClick(button);
                           }}
                         />
                <SimpleActionButtons
                    message={message}
                    onCopy={() => handleCopy(message.text)}
                    onRegenerate={() => {
                      const userMessageIndex = messages.findIndex(m => m.id === message.id) - 1;
                      const userMessage = messages[userMessageIndex];
                      if (userMessage && userMessage.sender === 'user') {
                        handleRegenerate(userMessage.text);
                      }
                    }}
                    onLike={() => handleFeedback(message.id, true)}
                    onDislike={() => handleFeedback(message.id, false)}
                    onDetails={() => handleDetails(message)}
                     />
                  </>
              )}
                     
                {/* Metadata info (opcional) */}
                {message.metadata?.processingTime && (
                  <div className="text-xs text-gray-500 mt-2">
                    ⏱️ {message.metadata.processingTime}ms
                  </div>
                )}
              </div>

                <div className={`flex items-center gap-2 mt-2 text-xs ${
                  message.sender === 'user' 
                    ? 'text-blue-100' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  <Clock className="w-3 h-3" />
                  <span>{message.timestamp.toLocaleTimeString()}</span>
                </div>
              </div>

              {message.sender === 'user' && (
                <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                </div>
              )}
            </div>
           ); 
          })}

          {/* Loading */}
          {isLoading && (
            <div className="flex gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                 <img 
                     src={IAAvatar} 
                     alt="KIKAI Técnico Caterpillar" 
                     className="w-6 h-6 object-contain"
                   />
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <Loader className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('chat.thinking')}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
          <form onSubmit={handleSendMessage} className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
               value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                 placeholder={
                  currentUser 
                    ? t('chat.placeholder')
                    : t('chat.placeholdererror')
                }
                disabled={isLoading}
                rows={1}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-500 disabled:opacity-50"
                style={{
                  minHeight: '52px',
                  maxHeight: '150px'
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement
                  target.style.height = 'auto'
                  target.style.height = `${Math.min(target.scrollHeight, 150)}px`
                }}
              />
              
                <div className={`absolute bottom-2 right-12 text-xs ${
                characterCount > maxCharacters 
                  ? 'text-red-500 font-medium' 
                  : isDark ? 'text-gray-500' : 'text-gray-400'
              }`}>
                {characterCount}/{maxCharacters}
              </div>
            </div>
            
            <button
              type="submit"
              disabled={!currentUser || isLoading || !newMessage.trim()}
              className="w-12 h-12 flex items-center justify-center bg-blue-600 hover:bg-blue-500 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-2xl transition-all disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>
      {/* Toast */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
      />
      </div>

      {/* ============================================= */}
      {/* 📜 CARD DEL HISTORIAL */}
      {/* ============================================= */}
      {historyOpen && (
        <div className="flex-[3] flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden transition-all duration-300">
          
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('chat.history.title')}
            </h2>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('chat.history.search')}
                value={searchHistory}
                onChange={(e) => setSearchHistory(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: 'calc(100vh - 20rem)' }}>
            {filteredHistory.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">{t('chat.history.empty')}</p>
              </div>
            ) : (
              filteredHistory.map(chat => (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleLoadChat(chat.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleLoadChat(chat.id)}
                  className={`w-full text-gray-900 p-3 mb-1 rounded-lg text-left dark:border-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group ${
                    currentChatId === chat.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{chat.title}</p>
                      <p className="text-xs opacity-70 mt-1">{new Date(chat.updated_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteChat(chat.id, e); }}
                      className="ml-2 p-1 opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-all"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
            <button
              onClick={createNewChat}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('chat.history.newChat')}
            </button>
          </div>
        </div>
      )}
      <DocumentSidebar
        isOpen={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          const lastMsg = messages[messages.length - 1];
          if (lastMsg) {
            setClosedSidebarMessageId(lastMsg.id);
          }
        }}
        documentData={sidebarDocument}
        isLoading={sidebarLoading}
        isDark={isDark}
        language={language}
        
      />
    </div>
  )
};
export default TechnicalChat;